import { CalendarDays } from "lucide-react";
import { useDispatch } from "react-redux";
import { setSelectedProduct } from "../../api/reducers/selectedProductReducer";
import { useNavigate } from "react-router-dom";
import { FormatDate } from "../../components/FormatDate";
import CartAmountToggle from "./CartAmountToggle";
import { PAYMENT } from "../s3-assets/Payment";

const CartSkeleton = ({
  item,
  quantity,
  currency,
  domain,
  setIncrease,
  setDecrease,
  period,
  schedule,
  associatedProducts = [],
}) => {
  const nav = useNavigate();
  const dispatch = useDispatch();

  const handleProductClick = () => {
    dispatch(setSelectedProduct(item));
    nav("/product-info");
  };
  return (
    <div className="mt-5 mb-10 w-full max-w-4xl mx-auto" id="product-card-container">
      <div className="flex flex-col md:flex-row bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="w-full md:w-1/3 p-2">
          <div className="h-full">
            {item?.imageURL ? (
              <img
                id="product-image"
                loading="lazy"
                src={item.imageURL}
                alt={item.name}
                className="w-full h-full object-contain rounded-none"
              />
            ) : (
              <div className="bg-gray-200 h-full flex items-center justify-center rounded-none" id="no-image-container">
                {domain === "INSTITUTION" ? (
                  <img id="institution-placeholder-image" src={PAYMENT.IMAGES.IMAGE_2} alt="Institution" />
                ) : (
                  <span id="no-image-text">No image</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col justify-between p-4 w-full md:w-2/3">
          <div className="flex justify-between items-start">
            <div>
              <h2
                id="product-name"
                className="text-lg font-bold truncate cursor-pointer hover:text-teal-600 transition-colors"
                onClick={handleProductClick}
              >
                {item && item.name}
              </h2>
              <div className="text-gray-500 truncate" id="product-description">
                {domain === "RETAIL" ? (
                  item?.description
                ) : (
                  <div className="flex items-center" id="schedule-info">
                    <CalendarDays size={18} className="mr-2 text-sky-500" />
                    <div>
                      <span className="text-sm font-medium text-gray-800" id="schedule-date">
                        {FormatDate(period?.effectiveStartDate)} -{" "}
                        {FormatDate(period?.effectiveEndDate)}
                      </span>
                      <div className="text-sm text-gray-700" id="schedule-days">
                        {schedule?.daysOfSchedule?.map((day, index) => (
                          <span key={index}>
                            {day.slice(0, 3)}
                            {index < schedule.daysOfSchedule.length - 1 ? ", " : ""}
                          </span>
                        ))}
                        {schedule?.scheduleCode && (
                          <div id="schedule-time">
                            {schedule.scheduleCode
                              .split("-")
                              .slice(1)
                              .map((time, index, array) => (
                                <span key={index}>
                                  {time.trim()}
                                  {index === array.length - 1
                                    ? ""
                                    : index === 0
                                      ? " to "
                                      : " "}
                                </span>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right" id="total-price-container">
              <h4 className="font-semibold text-gray-600">Total</h4>
              <p className="font-semibold text-teal-700" id="total-price">
                {currency}
                {item && (item?.price?.priceAmt * quantity).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Associated Products Table */}
          {associatedProducts.length > 0 && (
            <div className="mt-4" id="included-items-container">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Included Items:
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" id="included-items-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {associatedProducts.map((product, index) => (
                      <tr key={index} id={`associated-product-${index}`}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {product.name}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {product.quantity || 1}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {currency}
                          {product.price?.priceAmt?.toFixed(2) || '0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-2" id="cart-amount-toggle">
            {domain === "RETAIL" && (
              <CartAmountToggle
                quantity={quantity}
                setDecrease={() =>
                  setDecrease(item?.product?.primaryId, item?.price?.priceAmt)
                }
                setIncrease={() =>
                  setIncrease(item?.product?.primaryId, item?.price?.priceAmt)
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

};

export default CartSkeleton;
